import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { DynamicFieldType } from '../Utils/dynamicFieldMarkup';

/**
 * "Insert Field" — lets a clinic user create a brand-new synced field from the
 * template editor, no developer/release involved (the "dynamic fields" ask,
 * 17 Sep). Rebuilt fresh, not a resurrection of the old `InsertSmartFieldModal`.
 *
 * v1 (17 Sep) was deliberately text-only. v2 (18 Sep) adds the two things v1
 * left out on purpose:
 * - a field type picker (text / dropdown / number), with the clinic typing
 *   its own option list for a dropdown, or a unit for a number — never a
 *   CHAIN_FIELDS entry, this is still a clinic-defined field, not a new
 *   registered measurement.
 * - for a number field, an optional "auto-fill from" pick of an existing
 *   measurement already in this template, so the field starts pre-filled
 *   with that measurement's current value instead of blank. This is a
 *   one-time copy at insert time, not a live link — the dynamic field is
 *   independent of the source measurement from then on, same as typing
 *   the number in by hand.
 *
 * v3 (18 Sep) — this same dialog now also EDITS a field already in the
 * template, when the caller passes `editingFieldKey`. Fixing a typo in a
 * "Catch from SR" binding was previously only possible by deleting the whole
 * field and reinserting it from scratch — a typo'd binding would otherwise
 * sit silently broken with no way to correct it in place.
 */

export interface InsertDynamicFieldResult {
  fieldKey: string;
  label: string;
  fieldType: DynamicFieldType;
  options?: string[];
  unit?: string;
  initialValue?: string;
  /** Exact raw SR concept name (CodeMeaning) this field should catch at report-open. */
  srConcept?: string;
}

/** An existing measurement already present in this template, offered as an auto-fill source. */
export interface DynamicFieldAutoFillSource {
  key: string;
  label: string;
  value: string;
}

/** A dynamic field's current definition, as read off its span in the template — for editing. */
export interface DynamicFieldEditValues {
  label: string;
  fieldType: DynamicFieldType;
  options?: string[];
  unit?: string;
  srConcept?: string;
}

interface InsertDynamicFieldModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (result: InsertDynamicFieldResult) => void;
  /** Called on every label keystroke so the caller can preview/reserve the resulting fieldKey. */
  previewFieldKey: (label: string) => string;
  /** Measurements already in this template that a number field may auto-fill from. */
  autoFillSources?: DynamicFieldAutoFillSource[];
  /**
   * When set, the dialog edits this EXISTING field's definition instead of creating a
   * new one — the key stays fixed (renaming it would orphan every occurrence's sync),
   * only its label/type/options/unit/SR-binding change. `onConfirm` still fires, with
   * this same `fieldKey` in the result, so the caller can tell insert and edit apart.
   */
  editingFieldKey?: string;
  /** Prefills the dialog when `editingFieldKey` is set. Ignored otherwise. */
  editingValues?: DynamicFieldEditValues;
}

const InsertDynamicFieldModal: React.FC<InsertDynamicFieldModalProps> = ({
  isOpen,
  onCancel,
  onConfirm,
  previewFieldKey,
  autoFillSources = [],
  editingFieldKey,
  editingValues,
}) => {
  const isEditing = Boolean(editingFieldKey);
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<DynamicFieldType>('text');
  const [optionsText, setOptionsText] = useState('');
  const [unit, setUnit] = useState('');
  const [autoFillKey, setAutoFillKey] = useState('');
  const [srConcept, setSrConcept] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLabel(editingValues?.label ?? '');
      setFieldType(editingValues?.fieldType ?? 'text');
      setOptionsText((editingValues?.options ?? []).join('\n'));
      setUnit(editingValues?.unit ?? '');
      setAutoFillKey('');
      setSrConcept(editingValues?.srConcept ?? '');
    }
    // editingValues is a fresh object each time the caller opens the dialog for a
    // different field — isOpen alone would reuse whatever it was on the LAST open.
  }, [isOpen, editingValues]);

  if (!isOpen) {
    return null;
  }

  // Editing keeps the existing key fixed — never re-derive one from the (possibly
  // just-edited) label, or every other occurrence of this field loses its sync partner.
  const fieldKey = isEditing ? (editingFieldKey as string) : previewFieldKey(label);
  const options = optionsText
    .split(/[\n,]/)
    .map(o => o.trim())
    .filter(Boolean);
  const canInsert =
    label.trim().length > 0 && (fieldType !== 'dropdown' || options.length > 0);

  const handleInsert = () => {
    if (!canInsert) {
      return;
    }
    // Angle brackets stripped, not escaped — the label round-trips through a
    // plain HTML attribute read back with a simple regex, same reasoning as
    // the old modal's `safeLabel`.
    const safeLabel = label.trim().replace(/[<>]/g, '');
    const source = fieldType === 'number' ? autoFillSources.find(s => s.key === autoFillKey) : undefined;

    onConfirm({
      fieldKey,
      label: safeLabel,
      fieldType,
      options: fieldType === 'dropdown' ? options : undefined,
      unit: fieldType === 'number' ? unit.trim() || undefined : undefined,
      initialValue: source?.value,
      srConcept: srConcept.trim() || undefined,
    });
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(2, 8, 15, 0.72)', backdropFilter: 'blur(3px)' },
        },
        paper: {
          sx: theme => ({
            borderRadius: 3,
            border: `1px solid ${theme.palette.cardiac.line}`,
            backgroundImage: `linear-gradient(180deg, ${theme.palette.cardiac.surface2} 0%, ${theme.palette.cardiac.surface} 100%)`,
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
          }),
        },
      }}
    >
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18, color: 'text.primary', mb: 2 }}>
          {isEditing ? 'Edit field' : 'Insert field'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Label"
            placeholder="e.g. Fractional Shortening"
            value={label}
            onChange={e => setLabel(e.target.value)}
            autoFocus
            fullWidth
            size="small"
          />

          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Key: <code>{fieldKey || '—'}</code>
            {isEditing
              ? ' — fixed, so every existing copy of this field stays linked.'
              : ' — every copy of this field stays in sync wherever it appears in this template.'}
          </Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={fieldType}
            onChange={(_e, next) => next && setFieldType(next)}
            fullWidth
          >
            <ToggleButton value="text" sx={{ textTransform: 'none' }}>
              Text
            </ToggleButton>
            <ToggleButton value="dropdown" sx={{ textTransform: 'none' }}>
              Dropdown
            </ToggleButton>
            <ToggleButton value="number" sx={{ textTransform: 'none' }}>
              Number
            </ToggleButton>
          </ToggleButtonGroup>

          {fieldType === 'dropdown' && (
            <TextField
              label="Options"
              placeholder={'One per line, e.g.\nMild\nModerate\nSevere'}
              value={optionsText}
              onChange={e => setOptionsText(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              size="small"
              helperText="What the clinic will choose from — separate with a comma or a new line."
            />
          )}

          {fieldType === 'number' && (
            <>
              <TextField
                label="Unit (optional)"
                placeholder="e.g. cm, %"
                value={unit}
                onChange={e => setUnit(e.target.value)}
                fullWidth
                size="small"
              />
              {autoFillSources.length > 0 && (
                <TextField
                  select
                  label="Auto-fill from (optional)"
                  value={autoFillKey}
                  onChange={e => setAutoFillKey(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Starts the field with this measurement's current value. A one-time copy, not a live link."
                >
                  <MenuItem value="">— None, start blank —</MenuItem>
                  {autoFillSources.map(s => (
                    <MenuItem key={s.key} value={s.key}>
                      {s.label} ({s.value})
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}

          <TextField
            label="Catch from SR (advanced, optional)"
            placeholder="Exact measurement name from the scan machine's own report, e.g. XAEA12"
            value={srConcept}
            onChange={e => setSrConcept(e.target.value)}
            fullWidth
            size="small"
            helperText="If a scan's SR file has a measurement under this exact name, this field is filled from it automatically when a report is opened. Must match exactly — no wording tolerance."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onCancel}
            sx={theme => ({
              borderColor: theme.palette.cardiac.lineStrong,
              color: 'text.primary',
              textTransform: 'none',
              fontWeight: 600,
            })}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!canInsert}
            onClick={handleInsert}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {isEditing ? 'Save' : 'Insert'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default InsertDynamicFieldModal;
